import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);

  // Send heartbeat to track user activity
  const sendHeartbeat = useCallback(async () => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      try {
        await axios.post(`${API}/auth/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });
      } catch (error) {
        // Silently fail - don't interrupt user experience
      }
    }
  }, []);

  // Debounced heartbeat for user activity — fires at most once every 30 seconds
  const debouncedHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) return; // Already scheduled
    heartbeatTimeoutRef.current = setTimeout(() => {
      sendHeartbeat();
      heartbeatTimeoutRef.current = null;
    }, 30000);
  }, [sendHeartbeat]);

  // Start heartbeat interval when user is authenticated
  useEffect(() => {
    if (user && token) {
      // Send initial heartbeat
      sendHeartbeat();
      
      // Set up interval (every 2 minutes)
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 120000);
      
      // Debounced activity tracking
      window.addEventListener('click', debouncedHeartbeat);
      window.addEventListener('keypress', debouncedHeartbeat);
      
      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
        }
        window.removeEventListener('click', debouncedHeartbeat);
        window.removeEventListener('keypress', debouncedHeartbeat);
      };
    }
  }, [user, token, sendHeartbeat, debouncedHeartbeat]);

  const checkAuth = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(savedToken);
      }
    } catch (error) {
      const status = error?.response?.status;
      // Only clear auth on explicit 401/403 — not on network/server errors
      if (status === 401 || status === 403) {
        console.log('Auth check: token invalid, logging out');
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      } else {
        console.log('Auth check: transient error, keeping session', error?.message);
        // Keep existing token/user — might recover on next request
        const savedToken = localStorage.getItem('auth_token');
        if (savedToken) {
          setToken(savedToken);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    // Registration now returns pending status, not a token
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
      withCredentials: true
    });
    const userData = response.data;
    // Prefer JWT token over session token for consistent auth
    const authToken = userData.token || userData.session_token;
    if (authToken) {
      localStorage.setItem('auth_token', authToken);
      setToken(authToken);
    }
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (error) {
      console.log('Logout error:', error);
    }
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
